import cn from "@/v2-ui/utils/utils.cn"
import ManualLink from "@/v2-backoffice-client/components/manual/ManualLink"
import Page, { type PageProps } from "@/v2-backoffice-client/components/page"
import Tabs, { useTabParam } from "@/v2-backoffice-client/components/tabs"
import LicensePlanTabTrigger from "@/v2-backoffice-client/license/plan/LicensePlanTabTrigger"
import LicensePlanTable from "@/v2-backoffice-client/license/plan/LicensePlanTable"
import LicenseAccountTabTrigger from "@/v2-backoffice-client/license/account/LicenseAccountTabTrigger"
import LicenseAccountTable from "@/v2-backoffice-client/license/account/LicenseAccountTable"
import LicenseUnlicensedTabTrigger from "@/v2-backoffice-client/license/unlicensed/LicenseUnlicensedTabTrigger"
import LicenseUnlicensedTable from "@/v2-backoffice-client/license/unlicensed/LicenseUnlicensedTable"
import LicenseFeatureGroupTabTrigger from "@/v2-backoffice-client/license/featureGroup/LicenseFeatureGroupTabTrigger"
import LicenseFeatureAccess from "@/v2-backoffice-client/license/changes/LicenseFeatureAccess"
import LicenseChangesTabTrigger from "@/v2-backoffice-client/license/changes/LicenseChangesTabTrigger"
import LicenseFeatureGroupTable from "@/v2-backoffice-client/license/featureGroup/LicenseFeatureGroupTable"
import LicenseFeatureGroupCreateButton from "@/v2-backoffice-client/license/featureGroup/LicenseFeatureGroupCreateButton"
import LicenseAddOnTabTrigger from "@/v2-backoffice-client/license/addOns/catalog/LicenseAddOnTabTrigger"
import LicenseAddOnCatalogTable from "@/v2-backoffice-client/license/addOns/catalog/LicenseAddOnCatalogTable"
import LicenseAddOnCreateButton from "@/v2-backoffice-client/license/addOns/catalog/LicenseAddOnCreateButton"
import LicenseCollectionTabTrigger from "@/v2-backoffice-client/license/collections/LicenseCollectionTabTrigger"
import LicenseCollectionTable from "@/v2-backoffice-client/license/collections/LicenseCollectionTable"
import LicenseRenewalTabTrigger from "@/v2-backoffice-client/license/renewals/LicenseRenewalTabTrigger"
import LicenseRenewals from "@/v2-backoffice-client/license/renewals/LicenseRenewals"
import LicenseCoverageTabTrigger from "@/v2-backoffice-client/license/coverage/LicenseCoverageTabTrigger"
import LicenseCoverageBoard from "@/v2-backoffice-client/license/coverage/LicenseCoverageBoard"
import {
  LICENSING_TABS_ID,
  LICENSING_VIEW_ADD_ONS,
  LICENSING_VIEW_BILLING_ACCOUNTS,
  LICENSING_VIEW_CHANGES,
  LICENSING_VIEW_COLLECTIONS,
  LICENSING_VIEW_COVERAGE,
  LICENSING_VIEW_FEATURE_GROUPS,
  LICENSING_VIEW_PLANS,
  LICENSING_VIEW_RENEWALS,
  LICENSING_VIEW_UNLICENSED_USAGE,
} from "@/v2-backoffice-client/license/license.constants"

// Per view, behind the title's info toggle while that tab is active: the
// job the board does and where the fix happens, for the operator who asks.
const VIEW_DESCRIPTIONS: Record<string, string> = {
  [LICENSING_VIEW_PLANS]:
    "The licenses we sell, mirrored from Fenerum, and what each is worth. Give every license a product (until then the import skips its subscriptions and the row shows no customers) and seat semantics (so its seats count). Deprecate what we no longer sell.",
  [LICENSING_VIEW_BILLING_ACCOUNTS]:
    "Every Fenerum billing account the hourly mirror stores. Unmatched accounts are the worklist: each reason names the data fix, and the mirror heals the link on its own once fixed.",
  [LICENSING_VIEW_UNLICENSED_USAGE]:
    "Customers with live users and no covering license, hottest first. The remediation is a license grant from the customer panel.",
  [LICENSING_VIEW_FEATURE_GROUPS]:
    "The entitlement vocabulary: named feature packages that licenses and add-ons grant. Compose packages here, then attach them on a license panel's Packages tab or an add-on's Packages tab. A package decides access only for package-only features; its ticks on free features say where those go the day they are made package-only.",
  [LICENSING_VIEW_CHANGES]:
    "Who has what, decided in one place: every capability in Fusion and every base map and map layer. Each arrives free for every customer; open one to keep it free or make it package-only, naming the packages that include it.",
  [LICENSING_VIEW_ADD_ONS]:
    "Add-ons — extras a customer buys on top of a license, shared catalog or bespoke to one customer. Compose them here; they are added to a customer on the customer panel.",
  [LICENSING_VIEW_COLLECTIONS]:
    "Unpaid invoices worth chasing, straight from the hourly Fenerum mirror. Track the conversation per invoice — who was contacted, what they promised; rows resolve to Paid on their own when the money lands.",
  [LICENSING_VIEW_RENEWALS]:
    "The committed book projected 12 months forward: cash landing per month, renewals at risk, scheduled subscriptions arriving and churn leaving. A forecast from the Fenerum mirror — the moves themselves happen in Fenerum.",
  [LICENSING_VIEW_COVERAGE]:
    "Every license on a timeline — the Fenerum subscriptions behind it, the operator's own trial or grant, the invoice lines paying for it — with the places where those disagree flagged and the fix beside each: a hole between one subscription ending and the next starting, an invoice covering today with nothing live under it, a trial that ran out. Access is automated from billing; this is where a slip in Fenerum or HubSpot shows, with the manual override one click away.",
}

export type LicensingPageProps = PageProps

// The one licensing surface: licenses, billing accounts and unlicensed usage as
// sub-pages of a single page. Pure composition — each nav trigger owns the
// query behind its count/attention chip and each board owns its list query;
// the always-mounted triggers keep the chips live no matter which sub-page
// is open, and Apollo dedupes trigger + board into one request per list.
// Each board keeps its own toolbar, pills (namespaced URL params) and
// export; open panels (?v=) survive sub-page switches. Layout is the
// Features (translation) board pattern: header on top, then the sidebar
// card beside the boards inside the width-capped wrapper.
function LicensingPage(props: LicensingPageProps) {
  const { className, ...pageProps } = props

  const [activeView] = useTabParam(LICENSING_TABS_ID, LICENSING_VIEW_PLANS)

  return (
    <Page className={cn("licensing-page", className)} {...pageProps}>
      <Page.Header>
        <Page.Title
          about={
            VIEW_DESCRIPTIONS[activeView] ??
            VIEW_DESCRIPTIONS[LICENSING_VIEW_PLANS]
          }
        >
          Licensing
        </Page.Title>

        {/* Create actions live up here (the customers page's convention),
              swapped with the active sub-page — only the app-owned catalogs
              have one; the mirror-fed boards create nothing. */}
        <Page.Header.Actions>
          <ManualLink manual="licensing" />
          {activeView === LICENSING_VIEW_FEATURE_GROUPS && (
            <LicenseFeatureGroupCreateButton />
          )}
          {activeView === LICENSING_VIEW_ADD_ONS && (
            <LicenseAddOnCreateButton />
          )}
        </Page.Header.Actions>
      </Page.Header>

      <Page.Content className="flex-1 min-h-0 flex flex-col">
        <Tabs
          id={LICENSING_TABS_ID}
          defaultValue={LICENSING_VIEW_PLANS}
          variant="sidebar"
        >
          <Tabs.List>
            {/* The book as the Fenerum mirror shows it: the money and the
                accounts behind the licenses. */}
            <Tabs.Heading>Billing</Tabs.Heading>
            <LicenseRenewalTabTrigger />
            <LicenseCoverageTabTrigger />
            <LicenseAccountTabTrigger />
            <LicenseUnlicensedTabTrigger />
            <LicenseCollectionTabTrigger />

            {/* What we sell and gate — the entitlement vocabulary: plans,
                the packages they grant, add-ons, and what's new in Fusion
                that still needs a licensing decision. */}
            <Tabs.Heading>Catalog</Tabs.Heading>
            <LicensePlanTabTrigger />
            <LicenseFeatureGroupTabTrigger />
            <LicenseAddOnTabTrigger />
            <LicenseChangesTabTrigger />
          </Tabs.List>

          <Tabs.Content value={LICENSING_VIEW_PLANS} className="min-h-0">
            <LicensePlanTable />
          </Tabs.Content>

          <Tabs.Content value={LICENSING_VIEW_RENEWALS} className="min-h-0">
            <LicenseRenewals />
          </Tabs.Content>

          <Tabs.Content value={LICENSING_VIEW_COVERAGE} className="min-h-0">
            <LicenseCoverageBoard />
          </Tabs.Content>

          <Tabs.Content
            value={LICENSING_VIEW_BILLING_ACCOUNTS}
            className="min-h-0"
          >
            <LicenseAccountTable />
          </Tabs.Content>

          <Tabs.Content
            value={LICENSING_VIEW_UNLICENSED_USAGE}
            className="min-h-0"
          >
            <LicenseUnlicensedTable />
          </Tabs.Content>

          <Tabs.Content value={LICENSING_VIEW_COLLECTIONS} className="min-h-0">
            <LicenseCollectionTable />
          </Tabs.Content>

          <Tabs.Content
            value={LICENSING_VIEW_FEATURE_GROUPS}
            className="min-h-0"
          >
            <LicenseFeatureGroupTable />
          </Tabs.Content>

          <Tabs.Content value={LICENSING_VIEW_ADD_ONS} className="min-h-0">
            <LicenseAddOnCatalogTable />
          </Tabs.Content>

          <Tabs.Content value={LICENSING_VIEW_CHANGES} className="min-h-0">
            <LicenseFeatureAccess />
          </Tabs.Content>
        </Tabs>
      </Page.Content>
    </Page>
  )
}

export default LicensingPage
